<script lang="ts">
	import { getTranslate } from "@utils/getTranslate.js";
	import { openToast } from "@utils/openToast.js";
	import type { ClassValue } from "svelte/elements";
	import minusIcon from "@core/assets/icons/minus.svg?raw";
	import plusIcon from "@core/assets/icons/plus.svg?raw";
	import thumbsUpIcon from "@core/assets/icons/thumbs-up.svg?raw";
	import trashCanIcon from "@core/assets/icons/trash-can.svg?raw";
	import type { PurchasableCoupon } from "@core/schema/PurchasableCoupon.js";
	import { getCartPath } from "@core/schema/paths/getCartPath.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { Cart } from "../../cart/Cart.svelte.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import Formatted from "../common/Formatted.svelte";
	import Heading from "../common/Heading.svelte";
	import type { HeadingLevel } from "../common/HeadingLevel.js";
	import Icon from "../common/Icon.svelte";
	import { maxCartAdditionAmount } from "../maxCartAdditionAmount.js";
	import translations from "./PurchasableCouponCard.translations.json" with { type: "json" };

	const { tenant } = getGlobalTemplateProps();

	const {
		titleHeadingLevel = 3,
		purchasableCoupon,
		class: customClass,
	}: {
		titleHeadingLevel?: HeadingLevel;
		purchasableCoupon: PurchasableCoupon;
		class?: ClassValue;
	} = $props();

	const translate = getTranslate(translations);

	const formattedAmount = $derived(
		formatPrice(purchasableCoupon.amount, purchasableCoupon.currency, tenant.locale, false),
	);

	const count = $derived(Cart.orderPurchasedCoupons.find(({ id }) => id === purchasableCoupon.id)?.quantity ?? 0);

	function increment(): void {
		if (count >= maxCartAdditionAmount) {
			return;
		}
		const newlyAdded = count === 0;
		Cart.addPurchasedCoupon(purchasableCoupon, 1);
		openToast({
			group: "cart",
			icon: thumbsUpIcon,
			title: translate(newlyAdded ? "addedToCart" : "againAdded", { name: translate("voucher") }),
			onclick(): void {
				location.assign(getCartPath(tenant));
			},
		});
	}

	function decrement(): void {
		if (count === 0) {
			return;
		}
		Cart.setPurchasedCouponQuantity(purchasableCoupon, count - 1);
	}
</script>

<article class={["flex flex-col w-81.5", customClass]}>
	<div
		class="bg-fcrdarkgray text-white w-full aspect-square flex overflow-hidden flex-col justify-end p-4 rounded-2xl relative"
	>
		<div class="absolute inset-0 flex items-center justify-center">
			<span class="font-antipol text-3xl md:text-4xl font-medium">{formattedAmount}</span>
		</div>
		<div class="relative z-10 flex justify-end pointer-events-none">
			{#if count > 0}
				<button
					type="button"
					class={[
						"pointer-events-auto size-12 bg-white z-10 border border-fcrgray-300 flex items-center justify-center rounded-full",
						"[&:not(:disabled):hover]:bg-white/80",
						"[&:not(:disabled):hover:active]:scale-97 transition-transform duration-150 ease-in-out",
					]}
					onclick={decrement}
					aria-label={translate(count > 1 ? "removeOne" : "removeFromCart")}
				>
					<Icon icon={count > 1 ? minusIcon : trashCanIcon} class="size-6 text-fcrdarkgray" />
				</button>
				<output
					aria-live="polite"
					class={[
						"pointer-events-auto -mx-12 h-12 flex w-40 bg-fcrgray-300 rounded-full items-center justify-center",
						"text-fcrdarkgray text-lg/normal font-medium",
					]}
				>
					{count}
				</output>
			{/if}
			<button
				type="button"
				class={[
					"pointer-events-auto size-12 bg-white flex z-10 items-center justify-center rounded-full",
					"[&:not(:disabled):hover]:bg-white/80",
					"[&:not(:disabled):hover:active]:scale-97 transition-transform duration-150 ease-in-out",
				]}
				onclick={increment}
				aria-label={translate(count > 0 ? "addAnother" : "addToCart")}
			>
				<Icon icon={plusIcon} class="size-6 text-fcrdarkgray" />
			</button>
		</div>
	</div>
	<Heading
		level={titleHeadingLevel}
		text={translate("voucher")}
		class="font-antipol text-fcrdarkgray text-base md:text-1.5xl mt-4 md:mt-6"
	/>
	<div class="mt-1 md:mt-2 text-fcrdarkgray text-sm/normal md:text-base">
		<Formatted text={formattedAmount} />
	</div>
</article>
